import { useTranslation } from "react-i18next"
import {
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@nexus/ui"
import { PERSONA_SORT_OPTIONS, PERSONA_VALUE_TIER_OPTIONS } from "../../constants/personas.constants"
import { humanizePersonaIndustry } from "../../lib/persona-format"
import type { PersonaSortKey } from "../../types/personas.types"

/** "all" sentinel keeps the Select controlled without an empty-string value (Radix disallows it). */
export const PERSONA_FILTER_ALL = "all"

export type PersonaStatusFilter = "all" | "ACTIVE" | "SUGGESTED"

export interface PersonaFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  sortKey: PersonaSortKey
  onSortChange: (value: PersonaSortKey) => void
  industry: string
  onIndustryChange: (value: string) => void
  industryOptions: string[]
  valueTier: string
  onValueTierChange: (value: string) => void
  statusFilter: PersonaStatusFilter
  onStatusFilterChange: (value: PersonaStatusFilter) => void
  hasActiveFilters: boolean
  onClearAll: () => void
  /** Cards view uses the dropdown; table view sorts via column headers. */
  showSort?: boolean
}

/**
 * Filter controls for the personas library. Uses `display: contents` so each
 * control joins the parent toolbar wrap row instead of forming a nested flex
 * group that stretches across the page.
 */
export function PersonaFilters({
  search,
  onSearchChange,
  sortKey,
  onSortChange,
  industry,
  onIndustryChange,
  industryOptions,
  valueTier,
  onValueTierChange,
  statusFilter,
  onStatusFilterChange,
  hasActiveFilters,
  onClearAll,
  showSort = true,
}: PersonaFiltersProps) {
  const { t } = useTranslation()
  const searchPlaceholder = t("personas.filters.searchPlaceholder", "Search user personas...")
  const allIndustriesLabel = t("personas.filters.allIndustries", "All industries")
  const allValueTiersLabel = t("personas.filters.allValueTiers", "All value tiers")
  const allStatusesLabel = t("personas.filters.allStatuses", "All statuses")
  const sortLabel = t("personas.filters.sortLabel", "Sort by")
  const statusLabel = t("personas.filters.statusLabel", "Status")

  return (
    <div className="contents">
      <Input
        iconStart="search"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="h-8 w-52 shrink-0 text-sm"
        aria-label={searchPlaceholder}
      />

      {showSort ? (
        <Select value={sortKey} onValueChange={(v) => onSortChange(v as PersonaSortKey)}>
          <SelectTrigger className="h-8 w-[11.5rem] shrink-0 text-xs" aria-label={sortLabel}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERSONA_SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey, opt.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      <Select
        value={statusFilter}
        onValueChange={(v) => onStatusFilterChange(v as PersonaStatusFilter)}
      >
        <SelectTrigger
          className={cn(
            "h-8 w-[9rem] shrink-0 text-xs",
            statusFilter !== PERSONA_FILTER_ALL && "border-primary/40 bg-primary/5 text-foreground",
          )}
          aria-label={statusLabel}
        >
          <SelectValue placeholder={allStatusesLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={PERSONA_FILTER_ALL}>{allStatusesLabel}</SelectItem>
          <SelectItem value="ACTIVE">{t("personas.status.approved", "Approved")}</SelectItem>
          <SelectItem value="SUGGESTED">{t("personas.status.new", "New")}</SelectItem>
        </SelectContent>
      </Select>

      <Select value={industry} onValueChange={onIndustryChange}>
        <SelectTrigger
          className={cn(
            "h-8 w-[10rem] shrink-0 text-xs",
            industry !== PERSONA_FILTER_ALL && "border-primary/40 bg-primary/5 text-foreground",
          )}
          aria-label={allIndustriesLabel}
        >
          <SelectValue placeholder={allIndustriesLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={PERSONA_FILTER_ALL}>{allIndustriesLabel}</SelectItem>
          {industryOptions.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {humanizePersonaIndustry(opt)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={valueTier} onValueChange={onValueTierChange}>
        <SelectTrigger
          className={cn(
            "h-8 w-[9.5rem] shrink-0 text-xs",
            valueTier !== PERSONA_FILTER_ALL && "border-primary/40 bg-primary/5 text-foreground",
          )}
          aria-label={allValueTiersLabel}
        >
          <SelectValue placeholder={allValueTiersLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={PERSONA_FILTER_ALL}>{allValueTiersLabel}</SelectItem>
          {PERSONA_VALUE_TIER_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {t(opt.labelKey, opt.label)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters ? (
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex h-8 shrink-0 items-center gap-1 px-1 text-xs font-medium text-muted-foreground transition-colors duration-150 ease-out hover:text-foreground"
        >
          <Icon name="x" size="xs" />
          {t("common.clearFilters", "Clear filters")}
        </button>
      ) : null}
    </div>
  )
}
